/**
 * Technician Detail page per COMP-007 Story 4 Steps 2-3.
 * Shows profile summary, eligibility matrix (per-customer allow/deny with reasons).
 * Admin actions: edit attributes, revoke access.
 */

import { useState } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Users, ShieldCheck, ShieldX, Settings } from "lucide-react";
import { GlassCard } from "@/components/sc/glass-card";
import { StatusBadge } from "@/components/sc/status-badge";
import { Button } from "@/components/ui/button";
import { useTechnicianDetail } from "@/hooks/use-technicians";
import { EditAttributesDialog } from "@/components/technicians/edit-attributes-dialog";
import { RevokeAccessDialog } from "@/components/technicians/revoke-access-dialog";
import type { TechnicianSummary } from "@/types";

function LoadingSpinner({ resource }: { readonly resource: string }) {
  return (
    <GlassCard className="flex items-center justify-center py-16">
      <div className="text-center">
        <div className="mx-auto mb-2 h-6 w-6 rounded-full bg-sc-primary animate-sc-pulse" />
        <p className="text-body-sm text-sc-text-muted">Loading {resource}...</p>
      </div>
    </GlassCard>
  );
}

export function TechnicianDetailPage() {
  const { technicianId } = useParams({ strict: false }) as {
    technicianId: string;
  };
  const navigate = useNavigate();
  const detailQuery = useTechnicianDetail(technicianId);

  const [editOpen, setEditOpen] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);

  const data = detailQuery.data;

  if (detailQuery.isLoading) {
    return <LoadingSpinner resource="technician" />;
  }

  if (detailQuery.isError || !data) {
    return (
      <GlassCard className="text-center py-16">
        <Users size={48} className="mx-auto mb-4 text-sc-text-muted" />
        <h2 className="text-h3 font-display text-sc-navy mb-2">
          Unable to load technician data
        </h2>
        <p className="text-body-sm text-sc-text-muted mb-4">
          Microsoft Graph API may be experiencing issues.
        </p>
        <button
          onClick={() => void navigate({ to: "/technicians" })}
          className="text-body-sm text-sc-primary hover:underline"
        >
          Back to Technicians
        </button>
      </GlassCard>
    );
  }

  const { technician, customers } = data;
  const tier = "tier" in technician ? technician.tier : null;
  const clearance =
    "securityClearance" in technician ? technician.securityClearance : null;
  const location =
    "workLocation" in technician ? technician.workLocation : null;

  const eligible = customers.filter((c) => c.eligibility.allowed);
  const denied = customers.filter((c) => !c.eligibility.allowed);

  // Build a TechnicianSummary for the dialogs
  const techSummary: TechnicianSummary = {
    userId: technician.userId,
    displayName: technician.displayName,
    userPrincipalName: technician.userPrincipalName,
    tier,
    securityClearance: clearance,
    workLocation: location,
    accountEnabled: "accountEnabled" in technician ? technician.accountEnabled : true,
    teamsSpecialist: "teamsSpecialist" in technician ? (technician.teamsSpecialist as boolean) : false,
  };

  return (
    <div className="space-y-6">
      {/* Back */}
      <button
        onClick={() => void navigate({ to: "/technicians" })}
        className="flex items-center gap-1 text-body-sm text-sc-text-muted hover:text-sc-text transition-colors"
      >
        <ArrowLeft size={16} />
        Back to Technicians
      </button>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-h1 font-display text-sc-navy">
            {technician.displayName}
          </h1>
          <p className="text-body-sm text-sc-text-muted mt-1">
            {technician.userPrincipalName}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {tier ? (
            <StatusBadge status={tier} category="techTier" />
          ) : (
            <span className="text-body-sm text-sc-text-muted">
              Not configured
            </span>
          )}
        </div>
      </div>

      {/* Profile summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <GlassCard>
          <span className="text-label text-sc-text-muted uppercase tracking-widest">
            Tier
          </span>
          <p className="text-body text-sc-text font-medium mt-1">
            {tier ?? "Not set"}
          </p>
        </GlassCard>
        <GlassCard>
          <span className="text-label text-sc-text-muted uppercase tracking-widest">
            Clearance
          </span>
          <p className="text-body text-sc-text font-medium mt-1">
            {clearance ?? "Not set"}
          </p>
        </GlassCard>
        <GlassCard>
          <span className="text-label text-sc-text-muted uppercase tracking-widest">
            Location
          </span>
          <p className="text-body text-sc-text font-medium mt-1">
            {location ?? "Not set"}
          </p>
        </GlassCard>
      </div>

      {/* Admin actions */}
      <div className="flex items-center gap-3">
        <Button variant="secondary" size="sm" onClick={() => setEditOpen(true)}>
          <Settings size={16} />
          Edit Attributes
        </Button>
        <Button
          variant="destructive"
          size="sm"
          onClick={() => setRevokeOpen(true)}
        >
          <ShieldX size={16} />
          Revoke Access
        </Button>
      </div>

      {/* Eligibility summary */}
      <div className="flex items-center gap-4">
        <span className="flex items-center gap-1 text-body-sm text-sc-success">
          <ShieldCheck size={16} />
          {eligible.length} eligible
        </span>
        <span className="flex items-center gap-1 text-body-sm text-sc-danger">
          <ShieldX size={16} />
          {denied.length} denied
        </span>
      </div>

      {/* Eligibility matrix (Story 4 Step 3) */}
      <GlassCard noPadding>
        <div className="px-6 pt-4 pb-2">
          <h3 className="text-h3 font-display text-sc-navy">
            Eligibility Matrix
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" role="table">
            <thead>
              <tr className="border-b border-sc-border" role="row">
                <th
                  role="columnheader"
                  className="px-6 py-3 text-left text-table-header text-sc-text-muted uppercase tracking-wider"
                >
                  Customer
                </th>
                <th
                  role="columnheader"
                  className="px-6 py-3 text-left text-table-header text-sc-text-muted uppercase tracking-wider"
                >
                  Eligible
                </th>
                <th
                  role="columnheader"
                  className="px-6 py-3 text-left text-table-header text-sc-text-muted uppercase tracking-wider"
                >
                  Access Level
                </th>
                <th
                  role="columnheader"
                  className="px-6 py-3 text-left text-table-header text-sc-text-muted uppercase tracking-wider"
                >
                  Denial Reason
                </th>
              </tr>
            </thead>
            <tbody>
              {customers.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-12 text-center text-body-sm text-sc-text-muted"
                  >
                    No customer eligibility data available.
                  </td>
                </tr>
              ) : (
                customers.map((item) => (
                  <tr
                    key={item.customer.m365TenantId}
                    role="row"
                    className="border-b border-sc-border/50 last:border-b-0"
                  >
                    <td
                      role="cell"
                      className="px-6 py-3 text-body-sm text-sc-text font-medium"
                    >
                      {item.customer.customerName}
                    </td>
                    <td role="cell" className="px-6 py-3">
                      {item.eligibility.allowed ? (
                        <ShieldCheck
                          size={18}
                          className="text-sc-success"
                          aria-label="Eligible"
                        />
                      ) : (
                        <ShieldX
                          size={18}
                          className="text-sc-danger"
                          aria-label="Denied"
                        />
                      )}
                    </td>
                    <td
                      role="cell"
                      className="px-6 py-3 text-body-sm text-sc-text"
                    >
                      {item.eligibility.accessLevel ?? "—"}
                    </td>
                    <td role="cell" className="px-6 py-3">
                      {item.eligibility.failures.length > 0 ? (
                        <div className="space-y-1">
                          {item.eligibility.failures.map((f, idx) => (
                            <p
                              key={idx}
                              className="text-label text-sc-danger"
                            >
                              {f.requirement}: required {f.required}, got{" "}
                              {f.actual}
                            </p>
                          ))}
                        </div>
                      ) : (
                        <span className="text-body-sm text-sc-text-muted">
                          —
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </GlassCard>

      {/* Dialogs */}
      <EditAttributesDialog
        open={editOpen}
        technician={techSummary}
        onClose={() => setEditOpen(false)}
      />
      <RevokeAccessDialog
        open={revokeOpen}
        technician={techSummary}
        onClose={() => {
          setRevokeOpen(false);
          void navigate({ to: "/technicians" });
        }}
      />
    </div>
  );
}
